import { useEffect, useState } from 'preact/hooks';
import {
  downloads,
  getWifiOnly,
  hasNative,
  removeAllDownloads,
  setWifiOnly,
  storageUsage,
} from '../state/downloads';

/**
 * Download preferences, shown only inside the Android app. These are device settings rather than
 * account settings — they live in the shell's own storage, not in `/api/me` — so they are kept
 * out of the synced settings block above them.
 */
export function DownloadSettings() {
  // Hooks run unconditionally: `hasNative` flips once the bridge is detected, so an early return
  // above them would change the hook order between renders.
  const [wifiOnly, setWifiOnlyState] = useState(true);
  const [usage, setUsage] = useState<{ usedBytes: number; freeBytes: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const native = hasNative.value;
  const count = Object.values(downloads.value).filter((row) => row.status === 'COMPLETE').length;

  // Keyed on the completed count, not the downloads map: a progress push arrives every few
  // hundred milliseconds while something is downloading, and each one would otherwise fire two
  // more bridge round trips from a settings screen.
  useEffect(() => {
    if (!native) return;
    void getWifiOnly().then(setWifiOnlyState);
    void storageUsage().then(setUsage);
  }, [native, count]);

  if (!native) return null;

  const toggleWifi = async (value: boolean) => {
    setBusy(true);
    try {
      setWifiOnlyState(await setWifiOnly(value));
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    setBusy(true);
    try {
      await removeAllDownloads();
      setUsage(await storageUsage());
    } finally {
      setBusy(false);
    }
  };

  return (
    <section class="section">
      <h2 class="section__title">Downloads</h2>

      <label class="field">
        <span>Download over Wi-Fi only</span>
        <input
          type="checkbox"
          checked={wifiOnly}
          disabled={busy}
          onChange={(e) => void toggleWifi((e.target as HTMLInputElement).checked)}
        />
      </label>

      <p class="muted">
        {count === 0 ? 'Nothing downloaded yet.' : `${count} episode${count === 1 ? '' : 's'} on this device`}
        {usage ? ` · ${formatBytes(usage.usedBytes)} used, ${formatBytes(usage.freeBytes)} free` : ''}
      </p>

      {count > 0 && (
        <button type="button" class="button button--ghost" disabled={busy} onClick={() => void clear()}>
          Remove all downloads
        </button>
      )}
    </section>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`;
}
